import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Menu, 
  Users, 
  Settings,
  FileText,
  Globe,
  MessageSquare,
  BarChart3,
  QrCode,
  Clock,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'

const Sidebar = ({ isMobileOpen, setIsMobileOpen }: { isMobileOpen: boolean; setIsMobileOpen: (open: boolean) => void }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const { t } = useTranslation()
  const { user } = useAuthStore()

  // Define base navigation items
  const baseNavigation = [
    { 
      name: t('sidebar.dashboard'),
      href: '/', 
      icon: LayoutDashboard 
    },
    { 
      name: t('sidebar.menus'),
      href: '/menus', 
      icon: Menu 
    },
  ]

  const getNavigation = () => {
    if (!user) {
      return baseNavigation;
    }

    const navigation = [...baseNavigation];
    const isAdminUser = ['admin', 'super-admin', 'agency'].includes(user.role);
    const isRestaurantOwner = user.role === 'restaurant-owner';
    
    if (isAdminUser && !isRestaurantOwner) {
      navigation.splice(2, 0, {
        name: t('sidebar.users'),
        href: '/users', 
        icon: Users 
      });
    }
    
    return navigation;
  }

  const navigation = getNavigation()

  // Log the current URL and path for debugging
  console.log('Current origin:', window.location.origin);
  console.log('Logo path:', '/Menu.png');
  console.log('Full logo URL:', window.location.origin + '/Menu.png');

  return (
    <>
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col sidebar-transition ${collapsed ? 'lg:w-20' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-4">
            <div className="flex items-center">
              {logoError ? (
                // Fallback when logo fails to load
                <div className={`font-bold text-[#0A0C0B] ${collapsed ? 'text-xs' : 'text-lg'}`}>
                  {collapsed ? 'MD' : 'MenuDigitale'}
                </div>
              ) : (
                <img
                  src="/Menu.png"
                  alt="MenùDigitale"
                  className={`object-contain transition-all duration-300 ${
                    collapsed ? 'h-10 w-10' : 'h-12 w-auto max-w-[160px]'
                  }`}
                  onError={(e) => {
                    console.error('Logo failed to load at path: /Menu.png');
                    console.error('Current URL:', window.location.href);
                    setLogoError(true);
                  }}
                  onLoad={() => {
                    console.log('Logo loaded successfully!');
                  }}
                />
              )}
            </div>
            
            {/* Collapse button - desktop */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:block p-1 rounded-md hover:bg-gray-100 transition-colors"
              aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5 text-[#687d76]" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-[#687d76]" />
              )}
            </button>
          </div>
          
          <nav className="mt-8 flex-1 flex flex-col space-y-1 px-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gradient-to-r from-[#7BD5B5]/10 to-[#5ac39a]/10 text-[#0A0C0B] border border-[#7BD5B5]/20'
                      : 'text-[#687d76] hover:bg-[#F0F7F4] hover:text-[#0A0C0B]'
                  }`
                }
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${
                  collapsed ? 'mx-auto' : 'mr-3'
                }`} />
                {!collapsed && (
                  <span className="truncate">{item.name}</span>
                )}
              </NavLink>
            ))}
          </nav>
          
          {/* Sidebar footer */}
          {!collapsed && (
            <div className="px-4 pt-4 mt-auto border-t border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-[#7BD5B5] to-[#5ac39a] flex items-center justify-center">
                    <span className="text-white font-bold text-xs">KZ</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-[#0A0C0B]">
                    {t('sidebar.powered_by')} 
                  </p>
                  <p className="text-xs text-[#687d76]">
                    KM ZERO® WEBMARKETING 
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg border border-gray-200"
        aria-label={t('sidebar.toggle_menu')}
      >
        <Menu className="h-6 w-6 text-[#0A0C0B]" />
      </button>
      
      {/* Mobile sidebar overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      {/* Mobile sidebar */}
      <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-4">
            <div className="flex items-center">
              {logoError ? (
                <span className="text-lg font-bold text-[#0A0C0B]">MenuDigitale</span>
              ) : (
                <img
                  src="/Menu.png"
                  alt="MenùDigitale"
                  className="h-12 w-auto max-w-[160px] object-contain"
                  onError={(e) => {
                    console.error('Mobile logo failed to load');
                    setLogoError(true);
                  }}
                  onLoad={() => {
                    console.log('Mobile logo loaded successfully!');
                  }}
                />
              )}
            </div>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100"
              aria-label={t('sidebar.close')}
            >
              <X className="h-5 w-5 text-[#687d76]" />
            </button>
          </div>
          
          <nav className="mt-8 flex-1 flex flex-col space-y-1 px-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gradient-to-r from-[#7BD5B5]/10 to-[#5ac39a]/10 text-[#0A0C0B] border border-[#7BD5B5]/20'
                      : 'text-[#687d76] hover:bg-[#F0F7F4] hover:text-[#0A0C0B]'
                  }`}
                onClick={() => setIsMobileOpen(false)}
              >
                <item.icon className="h-5 w-5 flex-shrink-0 mr-3" />
                <span className="truncate">{item.name}</span>
              </NavLink>
            ))}
          </nav>
          
          <div className="px-4 pt-4 mt-auto border-t border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-[#7BD5B5] to-[#5ac39a] flex items-center justify-center">
                  <span className="text-white font-bold text-xs">KZ</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[#0A0C0B]">
                  {t('sidebar.powered_by')}
                </p>
                <p className="text-xs text-[#687d76]">
                  KM ZERO® WEBMARKETING
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar